
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

interface Registration {
  id: string;
  name: string;
  email: string;
  phone: string;
  course: string;
  company: string;
  participantCount: string;
  message: string;
  date: string;
  status: 'pending' | 'confirmed' | 'completed';
}

interface Course {
  id: string;
  date: string;
  type: string;
  time: string;
  spots: number;
  price: number;
  month: string;
}

export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState('courses');
  const [courses, setCourses] = useState<Course[]>([
    { id: '1', date: '8-9 Ocak', type: 'Temel İlkyardım Eğitimi', time: '09:00-17:00', spots: 12, price: 450, month: 'ocak' },
    { id: '2', date: '15 Ocak', type: 'Temel İlkyardım Güncelleme Eğitimi', time: '09:00-17:00', spots: 8, price: 250, month: 'ocak' },
    { id: '3', date: '22-23 Ocak', type: 'İlk Yardım Eğitici Eğitimi', time: '09:00-17:00', spots: 15, price: 650, month: 'ocak' },
  ]);

  const [registrations] = useState<Registration[]>([
    {
      id: '1',
      name: 'Ahmet Yılmaz',
      email: 'ahmet@email.com',
      phone: '0555 123 45 67',
      course: 'temel-ilkyardim',
      company: 'ABC Şirketi',
      participantCount: '5',
      message: 'Kurumsal eğitim almak istiyoruz.',
      date: '2024-01-15',
      status: 'pending'
    },
    {
      id: '2',
      name: 'Ayşe Demir',
      email: 'ayse@email.com',
      phone: '0555 987 65 43',
      course: 'yenileme',
      company: '',
      participantCount: '1',
      message: 'Sertifikamı yenilemek istiyorum.',
      date: '2024-01-14',
      status: 'confirmed'
    },
    {
      id: '3',
      name: 'Mehmet Kaya',
      email: 'mehmet@email.com',
      phone: '0555 456 78 90',
      course: 'temel-ilkyardim',
      company: 'XYZ Ltd.',
      participantCount: '10',
      message: 'Çalışanlarımız için toplu eğitim.',
      date: '2024-01-13',
      status: 'completed'
    }
  ]);

  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const handleEditCourse = (course: Course) => {
    setEditingCourse({ ...course });
  };

  const handleSaveCourse = () => {
    if (!editingCourse) return;
    
    setCourses(prev => 
      prev.map(course => 
        course.id === editingCourse.id ? editingCourse : course
      )
    );
    setEditingCourse(null);
  };

  const handleDeleteCourse = (id: string) => {
    if (confirm('Bu eğitimi silmek istediğinizden emin misiniz?')) {
      setCourses(prev => prev.filter(course => course.id !== id));
    }
  };

  const handleAddCourse = () => {
    const newCourse: Course = {
      id: Date.now().toString(),
      date: '',
      type: 'Temel İlkyardım Eğitimi',
      time: '09:00-17:00',
      spots: 20,
      price: 450,
      month: 'ocak'
    };
    setCourses(prev => [...prev, newCourse]);
    setEditingCourse(newCourse);
    setShowAddForm(false);
  };

  const getCourseTypeText = (type: string) => {
    const types: { [key: string]: string } = {
      'temel-ilkyardim': 'Temel İlkyardım Eğitimi',
      'yenileme': 'Temel İlkyardım Güncelleme Eğitimi',
      'egitici': 'İlk Yardım Eğitici Eğitimi',
      'yuksekte-calisma': 'Yüksekte Çalışma Eğitimi',
      'yangin': 'Yangın Eğitimi',
      'hijyen': 'Hijyen Eğitimi',
      'mesleki': 'Mesleki Eğitim',
      'myk': 'MYK Danışmanlığı'
    };
    return types[type] || type;
  };

  const getStatusText = (status: string) => {
    const statuses: { [key: string]: string } = {
      'pending': 'Beklemede',
      'confirmed': 'Onaylandı',
      'completed': 'Tamamlandı'
    };
    return statuses[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: string } = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'confirmed': 'bg-green-100 text-green-800',
      'completed': 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-lg sticky top-0 z-50">
        <div className="container mx-auto px-6">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/69983b9b992309f9f345f22b715baa7c/d976ad48ea6e824eb2b305836503c616.jfif" 
                alt="ASSA Logo" 
                className="h-12 w-auto"
              />
            </Link>
            <div className="flex items-center space-x-6">
              <Link 
                to="/" 
                className="text-gray-700 hover:text-blue-600 cursor-pointer flex items-center"
              >
                <i className="ri-home-line mr-2"></i>
                Ana Sayfa
              </Link>
              <div className="text-blue-600 font-semibold">Admin Panel</div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Yönetici Paneli</h1>
          <p className="text-gray-600">Eğitimleri ve kayıtları yönetin</p>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('courses')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'courses'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } cursor-pointer whitespace-nowrap`}
              >
                <i className="ri-calendar-line mr-2"></i>
                Eğitim Programları
              </button>
              <button
                onClick={() => setActiveTab('registrations')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'registrations'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } cursor-pointer whitespace-nowrap`}
              >
                <i className="ri-user-line mr-2"></i>
                Kayıtlar ({registrations.length})
              </button>
            </nav>
          </div>
        </div>

        {/* Courses Tab */}
        {activeTab === 'courses' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Eğitim Programları</h2>
              <button
                onClick={handleAddCourse}
                className="bg-blue-600 hover:bg-blue-7 0 text-white px-4 py-2 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center"
              >
                <i className="ri-add-line mr-2"></i>
                Yeni Eğitim Ekle
              </button>
            </div>

            <div className="grid gap-6">
              {courses.map((course) => (
                <div key={course.id} className="bg-white rounded-lg shadow-md p-6">
                  {editingCourse?.id === course.id ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Tarih
                        </label>
                        <input
                          type="text"
                          value={editingCourse.date}
                          onChange={(e) => setEditingCourse(prev => prev ? {...prev, date: e.target.value} : null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="8-9 Ocak"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Eğitim Türü
                        </label>
                        <select
                          value={editingCourse.type}
                          onChange={(e) => setEditingCourse(prev => prev ? {...prev, type: e.target.value} : null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                        >
                          <option value="Temel İlkyardım Eğitimi">Temel İlkyardım Eğitimi</option>
                          <option value="Temel İlkyardım Güncelleme Eğitimi">Temel İlkyardım Güncelleme Eğitimi</option>
                          <option value="İlk Yardım Eğitici Eğitimi">İlk Yardım Eğitici Eğitimi</option>
                          <option value="Yüksekte Çalışma Eğitimi">Yüksekte Çalışma Eğitimi</option>
                          <option value="Yangın Eğitimi">Yangın Eğitimi</option>
                          <option value="Hijyen Eğitimi">Hijyen Eğitimi</option>
                          <option value="Mesleki Eğitim">Mesleki Eğitim</option>
                          <option value="MYK Danışmanlığı">MYK Danışmanlığı</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Saat
                        </label>
                        <input
                          type="text"
                          value={editingCourse.time}
                          onChange={(e) => setEditingCourse(prev => prev ? {...prev, time: e.target.value} : null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="09:00-17:00"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Kontenjan
                        </label>
                        <input
                          type="number"
                          value={editingCourse.spots}
                          onChange={(e) => setEditingCourse(prev => prev ? {...prev, spots: parseInt(e.target.value)} : null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Fiyat (₺)
                        </label>
                        <input
                          type="number"
                          value={editingCourse.price}
                          onChange={(e) => setEditingCourse(prev => prev ? {...prev, price: parseInt(e.target.value)} : null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Ay
                        </label>
                        <select
                          value={editingCourse.month}
                          onChange={(e) => setEditingCourse(prev => prev ? {...prev, month: e.target.value} : null)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                        >
                          <option value="ocak">Ocak</option>
                          <option value="subat">Şubat</option>
                          <option value="mart">Mart</option>
                          <option value="nisan">Nisan</option>
                        </select>
                      </div>
                      <div className="md:col-span-2 lg:col-span-3 flex space-x-3 pt-4">
                        <button
                          onClick={handleSaveCourse}
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center"
                        >
                          <i className="ri-save-line mr-2"></i>
                          Kaydet
                        </button>
                        <button
                          onClick={() => setEditingCourse(null)}
                          className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center"
                        >
                          <i className="ri-close-line mr-2"></i>
                          İptal
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center">
                      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 flex-1">
                        <div>
                          <div className="text-sm text-gray-500">Tarih</div>
                          <div className="font-semibold text-gray-800">{course.date}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">Eğitim</div>
                          <div className="font-semibold text-gray-800">{course.type}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">Saat / Kontenjan</div>
                          <div className="font-semibold text-gray-800">{course.time} / {course.spots} kişi</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">Fiyat</div>
                          <div className="font-semibold text-gray-800">₺{course.price}</div>
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => handleEditCourse(course)}
                          className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-colors cursor-pointer"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          onClick={() => handleDeleteCourse(course.id)}
                          className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Registrations Tab */}
        {activeTab === 'registrations' && (
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Kayıtlar</h2>
            
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Kişi Bilgileri
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Eğitim
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Firma / Katılımcı
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tarih
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Durum
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {registrations.map((registration) => (
                      <tr key={registration.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{registration.name}</div>
                            <div className="text-sm text-gray-500">{registration.email}</div>
                            <div className="text-sm text-gray-500">{registration.phone}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{getCourseTypeText(registration.course)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{registration.company || 'Bireysel'}</div>
                          <div className="text-sm text-gray-500">{registration.participantCount} kişi</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(registration.date).toLocaleDateString('tr-TR')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(registration.status)}`}>
                            {getStatusText(registration.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button className="text-blue-600 hover:text-blue-900 mr-3 cursor-pointer">
                            <i className="ri-eye-line"></i>
                          </button>
                          <button className="text-green-600 hover:text-green-900 mr-3 cursor-pointer">
                            <i className="ri-phone-line"></i>
                          </button>
                          <button className="text-red-600 hover:text-red-900 cursor-pointer">
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {registrations.length === 0 && (
              <div className="text-center py-12">
                <i className="ri-inbox-line text-4xl text-gray-400 mb-4"></i>
                <p className="text-gray-500">Henüz kayıt bulunmuyor.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="container mx-auto px-6 text-center">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 mb-4 md:mb-0">
              © 2024 Assa İş Eğitimleri Akademisi Admin Panel
            </p>
            <Link
              to="/"
              className="text-gray-400 hover:text-white cursor-pointer"
            >
              Ana Sayfaya Dön
            </Link>
          </div>
        </div>
      </footer>

      {/* WhatsApp Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <a
          href="https://wa.me/+905324196401"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-green-500 hover:bg-green-600 text-white w-16 h-16 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 animate-pulse cursor-pointer"
          title="WhatsApp ile iletişime geç"
        >
          <i className="ri-whatsapp-line text-2xl"></i>
        </a>
      </div>
    </div>
  );
}
